import React, { useState, useEffect, useRef } from "react";
import getApiBaseUrl from "../utils/api";

function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let particlesArray = [];
    const numberOfParticles = 80;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = 2;
        this.speedX = (Math.random() - 0.5) * 1;
        this.speedY = (Math.random() - 0.5) * 1;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;

        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }

      draw() {
        ctx.fillStyle = "#00ffcc";
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function init() {
      particlesArray = [];
      for (let i = 0; i < numberOfParticles; i++) {
        particlesArray.push(new Particle());
      }
    }

    function connectParticles() {
      for (let a = 0; a < particlesArray.length; a++) {
        for (let b = a; b < particlesArray.length; b++) {
          let dx = particlesArray[a].x - particlesArray[b].x;
          let dy = particlesArray[a].y - particlesArray[b].y;
          let distance = dx * dx + dy * dy;

          if (distance < 12000) {
            ctx.strokeStyle = "rgba(0,255,200,0.15)";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }
      }
    }

    let animationId;
    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesArray.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      connectParticles();
      animationId = requestAnimationFrame(animate);
    }

    init();
    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();

    // ===== M6 FIX (CWE-1051 — Initialization with Hard-Coded Network
    // Resource Configuration Data / Admin Credentials Sent to Incorrect
    // Local Endpoint) =====
    // This request used to be hardcoded to "http://localhost:5008/...".
    // In any deployed build (testx.cc, a staging server, etc.) that
    // means the admin username/password get POSTed to the visiting
    // ADMIN'S OWN localhost:5008 — not the real backend — so the
    // request either fails outright or, worse, silently goes to
    // whatever happens to be listening on that admin's local port 5008.
    // Using the same environment-aware base URL as every other API call
    // in the app ensures admin credentials always go to the real,
    // correct backend for the environment the app is actually running in.
    const API_URL = getApiBaseUrl();

    let response, data;
    try {
      response = await fetch(`${API_URL}/auth/admin/login`, {
        method: "POST",
        // L7 FIX (CWE-602): required so the browser actually stores the
        // HttpOnly admin session cookie the server now sets on a
        // successful login, and sends it back on later admin requests.
        // Without this, the cookie set by Set-Cookie is silently
        // dropped and every subsequent adminAuth-protected request
        // fails as if the login had never happened.
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      data = await response.json();
    } catch (err) {
      alert("Could not connect to the server. Please try again.");
      return;
    }

    // ===== M4 FIX (CWE-532 — Sensitive Data in Application Logs) =====
    // `console.log(data)` used to print the full login response —
    // including the admin's JWT session token — into the browser
    // console, where it can be scraped by any injected script, a
    // malicious extension, or simply left visible on a shared machine.
    // Removed; nothing about a successful admin login needs to be
    // logged client-side.

    if (response.ok) {
      onLogin(data);
    } else {
      alert(data.msg || "Login failed.");
    }
  };

  return (
    <>
      <div className="login-container">
        <canvas ref={canvasRef} className="neural-canvas"></canvas>

        <div className="login-box">
          <h2>Admin Login</h2>

          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Enter Username"
              required
              onChange={(e) => setUsername(e.target.value)}
            />

            <input
              type="password"
              placeholder="Enter Password"
              required
              onChange={(e) => setPassword(e.target.value)}
            />

            <button type="submit">Login</button>
          </form>
        </div>
      </div>

      {/* ✅ CSS MOVED INSIDE COMPONENT */}
      <style>{`
        body {
          margin: 0;
          font-family: Arial, sans-serif;
          background: #000;
          color: #fff;
          overflow: hidden;
        }

        * {
          box-sizing: border-box;
        }

        .login-container {
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .neural-canvas {
          position: absolute;
          top: 0;
          left: 0;
          z-index: 0;
          background: radial-gradient(circle at center, #050505 0%, #000000 100%);
        }

        .login-box {
          position: relative;
          z-index: 2;
          background: #111;
          padding: 40px 30px;
          width: 320px;
          border-radius: 12px;
          text-align: center;
          border: 1px solid #00cc66;
          box-shadow: 0 0 15px rgba(0, 204, 102, 0.3);
        }

        .login-box h2 {
          margin-bottom: 20px;
        }

        .login-box input {
          width: 100%;
          padding: 12px;
          margin: 12px 0;
          background: #1a1a1a;
          border: 1px solid #00f7ff;
          border-radius: 6px;
          color: #fff;
          transition: 0.3s;
        }

        .login-box input:focus {
          outline: none;
          border-color: #00cc66;
          box-shadow: 0 0 5px #00cc66;
        }

        .login-box button {
          width: 100%;
          padding: 12px;
          margin-top: 16px;
          background: #00cc66;
          border: none;
          color: #fff;
          border-radius: 6px;
          cursor: pointer;
          font-weight: bold;
          transition: 0.3s;
        }

        .login-box button:hover {
          background: #00994d;
          box-shadow: 0 0 10px #00cc66;
        }
      `}</style>
    </>
  );
}
export default AdminLogin;